import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const services = [
  {
    icon: "🧠",
    title: "Individual Therapy",
    desc: "One-on-one sessions addressing anxiety, depression, trauma, stress, and personal growth.",
  },
  {
    icon: "💑",
    title: "Couples Counseling",
    desc: "Rebuilding trust, communication, and emotional connection between partners.",
  },
  {
    icon: "👨‍👩‍👧",
    title: "Family Therapy",
    desc: "Resolving family conflicts and improving relationship dynamics through guided group sessions.",
  },
  {
    icon: "🎯",
    title: "Career & Life Counseling",
    desc: "Helping individuals navigate major life transitions, career decisions, and self-discovery.",
  },
  {
    icon: "💡",
    title: "Cognitive Behavioral Therapy (CBT)",
    desc: "Evidence-based techniques to identify and reshape negative thought patterns.",
  },
  {
    icon: "🌿",
    title: "Stress & Anxiety Management",
    desc: "Practical, personalized tools and therapies to manage everyday stress effectively.",
  },
];

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      id="services"
      data-testid="services-section"
      ref={ref}
      className="relative py-24 md:py-32 px-6"
      style={{ background: "var(--color-bg)" }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p
            className="font-body italic tracking-[0.3em] text-xs uppercase mb-4"
            style={{ color: "var(--color-accent)" }}
          >
            — What We Offer —
          </p>
          <h2
            className="font-display font-bold"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.25rem)",
              color: "var(--color-text-dark)",
            }}
            data-testid="services-title"
          >
            OUR PSYCHOLOGICAL SERVICES
          </h2>
          <div
            className="w-20 h-[3px] mx-auto mt-6 rounded-full"
            style={{ background: "var(--color-accent)" }}
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className="group rounded-2xl p-8 transition-all duration-300 bg-white"
              style={{
                border: "1px solid #e3efe9",
                boxShadow: "0 10px 28px -18px rgba(31,51,41,0.15)",
              }}
              data-testid={`service-card-${i}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-accent)";
                e.currentTarget.style.boxShadow =
                  "0 20px 40px -18px rgba(107,158,140,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e3efe9";
                e.currentTarget.style.boxShadow =
                  "0 10px 28px -18px rgba(31,51,41,0.15)";
              }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-2xl mb-5"
                style={{
                  background: "var(--color-secondary)",
                  border: "1px solid #cfe1d8",
                }}
              >
                {s.icon}
              </div>
              <h3
                className="font-display font-bold text-xl md:text-2xl mb-3"
                style={{ color: "var(--color-text-dark)" }}
              >
                {s.title}
              </h3>
              <p
                className="font-body text-sm md:text-base leading-relaxed"
                style={{ color: "var(--color-text-light)" }}
              >
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
